// File: src/lib/graphql/queries/employee.ts
// Purpose: Employee Management GraphQL Queries and Mutations
// VIBE - Employee API

import { gql } from 'graphql-request'
import { executeGraphQL } from '../client'
import type { Department, Designation } from './organization'
import { Employee } from './auth'

// ============================================
// TYPES
// ============================================

export interface EmployeeDetails extends Employee {
  skills?: string[]
  certifications?: string[]
  projects?: Project[]
  tasks?: Task[]
  reportingTo?: Employee
  teamMembers?: Employee[]
  azureGroups?: string[]
}

export interface AzureAuthConfig {
  clientId: string
  tenantId: string
  redirectUri: string
  scopes: string[]
}

export interface UpdateEmployeeInput {
  _id: string
  firstName?: string
  lastName?: string
  email?: string
  countryCode?: string
  mobile_number?: string
  languages?: string[]
  joining_date?: string
  skills?: string[]
  designation?: string
  department?: string
  role?: string
  shift?: string
  is_active?: boolean
  hourlyRate?: number
  overtimeRate?: number
  employee_left?: boolean
  leaving_date?: string
}

export interface MarkEmployeeLeftInput {
  employeeId: string
  leaving_date: string
}

export interface Project {
  id: string
  name: string
  code: string
  description?: string
}

export interface Task {
  id: string
  title: string
  status: string
}

export interface EmployeeCost {
  _id: string
  employee?: Employee
  effectiveDate: string
  endDate?: string
  baseSalary: number
  currency: string
  payFrequency: string
  totalMonthlyCost: number
  totalAnnualCost: number
  status: string
  approvalDate?: string
  notes?: string
  createdAt: string
  updatedAt: string
  costComponents?: CostComponents
}

export interface CostComponents {
  allowances: {
    housing?: number
    transport?: number
    meal?: number
    medical?: number
  }
  deductions: {
    tax?: number
    socialSecurity?: number
    insurance?: number
  }
  employerContributions: {
    socialSecurity?: number
    insurance?: number
    retirement?: number
  }
}

export interface CreateEmployeeCostInput {
  employee?: string
  effectiveDate?: string
  endDate?: string
  baseSalary?: number
  currency?: string
  payFrequency?: string
  costComponents?: CostComponents
  notes?: string
}

export interface UpdateEmployeeCostInput {
  effectiveDate?: string
  endDate?: string
  baseSalary?: number
  currency?: string
  payFrequency?: string
  costComponents?: CostComponents
  notes?: string
}

export interface EmployeeCostFilter {
  employee?: string
  status?: string
  effectiveDateFrom?: string
  effectiveDateTo?: string
}

export interface EmployeeCostsResponse {
  nextCursor?: string
  edges: EmployeeCost[]
}

export interface ProjectEmployeeCost {
  employeeId: string
  projectId: string
  totalHours: number
  totalCost: number
  taskBreakdown: {
    date: string
    description: string
    hours: number
    cost: number
  }[]
  costBreakdown: {
    laborCost: number
    allowances: number
    employerContributions: number
    currency: string
  }
}

export interface ProjectCostSummary {
  projectId: string
  totalCost: number
  totalHours: number
  employeeCosts: {
    employee: {
      _id: string
      firstName: string
      lastName: string
      email: string
    }
    hours: number
    cost: number
    taskCount: number
  }[]
  periodBreakdown: {
    month: string
    hours: number
    cost: number
  }[]
}

export interface EmployeeProjectAllocation {
  employeeId: string
  month: number
  year: number
  totalHours: number
  totalCost: number
  projectAllocations: {
    project: {
      _id: string
      name: string
      code: string
    }
    hours: number
    percentage: number
    cost: number
  }[]
}

export interface CalculateEmployeeHourInput {
  employeeId?: string
  month?: number
  year?: number
  hourlyRate?: number
  overtimeRate?: number
}

export interface EmployeeHourCalculation {
  id: string
  month: number
  year: number
  totalRegularHours: number
  totalOvertimeHours: number
  totalWorkingDays: number
  presentDays: number
  absentDays: number
  holidays: number
  weekends: number
  leaves: number
  hourlyRate: number
  overtimeRate: number
  regularCost: number
  overtimeCost: number
  totalCost: number
  attendanceIds: string[]
  status: string
  approvalStatus: string
  approvalRemarks?: string
  remarks?: string
  createdAt: string
  updatedAt: string
  employee: {
    _id: string
    firstName: string
    lastName: string
    email: string
  }
}

export interface EmployeeHourByMonth {
  id: string
  month: number
  year: number
  totalRegularHours: number
  totalOvertimeHours: number
  totalWorkingDays: number
  presentDays: number
  absentDays: number
  holidays: number
  weekends: number
  leaves: number
  hourlyRate: number
  overtimeRate: number
  regularCost: number
  overtimeCost: number
  totalCost: number
  attendanceIds: string[]
  status: string
  approvalStatus: string
  approvalRemarks?: string
  remarks?: string
  createdAt: string
  updatedAt: string
  employee: {
    azureId?: string
    azureTenantId?: string
    azureEmail?: string
    azureDisplayName?: string
    ssoEnabled?: boolean
    lastAzureLogin?: string
    azureGroups?: string[]
    _id: string
    firstName: string
    lastName: string
    email: string
    countryCode?: string
    mobile_number?: string
    languages?: string[]
    joining_date?: string
    skills?: string[]
    designation?: string
    department?: string
    shift?: string
    hourlyRate?: number
    overtimeRate?: number
    is_active: boolean
    is_deleted?: boolean
    resume:string|null
    createdAt: string
    updatedAt: string
  }
}

// ============================================
// QUERIES
// ============================================

export const ALL_EMPLOYEES_QUERY = gql`
  query Employees {
    employees {
      nextCursor
      edges {
        _id
        firstName
        lastName
      }
    }
  }
`

export const EMPLOYEES_QUERY = gql`
  query Employees($limit: Int, $department: String, $designation: String) {
    employees(limit: $limit, department: $department, designation: $designation) {
      nextCursor
      edges {
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        is_active
        is_deleted
        createdAt
        updatedAt
        hourlyRate
        overtimeRate
        employee_left
        leaving_date
        role {
          _id
          title
          is_active
        }
        leave_requests {
          leveId
          status
          description
        }
        documents {
          url
          type
          valid
        }
        resume
      }
    }
  }
`

export const EMPLOYEE_QUERY = gql`
  query Employee($id: ID!) {
    employee(id: $id) {
      _id
      firstName
      lastName
      email
      countryCode
      mobile_number
      languages
      joining_date
      skills
      designation
      department
      role {
        _id
        title
        is_active
      }
      shift
      hourlyRate
      overtimeRate
      reportsTo {
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        role {
          _id
          title
          is_active
        }
        designation
        department
      }
      is_active
      createdAt
      updatedAt
    }
  }
`

export const EMPLOYEES_BY_ROLE_QUERY = gql`
  query EmployeesByRole($role: String!, $first: Int, $after: String) {
    employeesByRole(role: $role, first: $first, after: $after) {
      edges {
        node {
          id
          employeeId
          firstName
          lastName
          email
          designation {
            id
            name
            code
          }
          department {
            id
            name
            code
          }
          isActive
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

export const EMPLOYEE_BY_AZURE_ID_QUERY = gql`
  query EmployeeByAzureId($azureId: String!) {
    employeeByAzureId(azureId: $azureId) {
      id
      employeeId
      firstName
      lastName
      email
      azureId
      azureGroups
      isActive
    }
  }
`

export const CHECK_EMPLOYEE_AZURE_ACCESS_QUERY = gql`
  query CheckEmployeeAzureAccess($azureId: String!) {
    checkEmployeeAzureAccess(azureId: $azureId) {
      hasAccess
      employee {
        id
        employeeId
        firstName
        lastName
        email
      }
      requiredGroups
      userGroups
    }
  }
`

export const AZURE_AUTH_CONFIG_QUERY = gql`
  query AzureAuthConfig {
    azureAuthConfig {
        clientId
        redirectUrl
        tenantId
        enabled
    }
}
`

export const EMPLOYEE_COSTS_QUERY = gql`
  query EmployeeCosts($limit: Int, $cursor: String, $filter: EmployeeCostFilter) {
    employeeCosts(limit: $limit, cursor: $cursor, filter: $filter) {
      nextCursor
      edges {
        _id
        effectiveDate
        endDate
        baseSalary
        currency
        payFrequency
        totalMonthlyCost
        totalAnnualCost
        status
        approvalDate
        notes
        createdAt
        updatedAt
        employee {
          _id
          firstName
          lastName
          email
          countryCode
          mobile_number
          languages
          joining_date
          skills
          designation
          department
          role {
            _id
            title
            is_active
          }
          is_active
          createdAt
          updatedAt
        }
        costComponents {
          allowances {
            housing
            transport
            meal
            medical
          }
          deductions {
            tax
            socialSecurity
            insurance
          }
          employerContributions {
            socialSecurity
            insurance
            retirement
          }
        }
      }
    }
  }
`

export const PROJECT_EMPLOYEE_COST_QUERY = gql`
  query ProjectEmployeeCost(
    $employeeId: ID!
    $projectId: ID!
    $startDate: String!
    $endDate: String!
  ) {
    projectEmployeeCost(
      employeeId: $employeeId
      projectId: $projectId
      startDate: $startDate
      endDate: $endDate
    ) {
      employeeId
      projectId
      totalHours
      totalCost
      taskBreakdown {
        date
        description
        hours
        cost
      }
      costBreakdown {
        laborCost
        allowances
        employerContributions
        currency
      }
    }
  }
`

export const PROJECT_COST_SUMMARY_QUERY = gql`
  query ProjectCostSummary(
    $projectId: ID!
    $startDate: String!
    $endDate: String!
  ) {
    projectCostSummary(
      projectId: $projectId
      startDate: $startDate
      endDate: $endDate
    ) {
      projectId
      totalCost
      totalHours
      employeeCosts {
        employee {
          _id
          firstName
          lastName
          email
        }
        hours
        cost
        taskCount
      }
      periodBreakdown {
        month
        hours
        cost
      }
    }
  }
`

export const EMPLOYEE_PROJECT_ALLOCATION_QUERY = gql`
  query EmployeeProjectAllocation(
    $employeeId: ID!
    $month: Int!
    $year: Int!
  ) {
    employeeProjectAllocation(
      employeeId: $employeeId
      month: $month
      year: $year
    ) {
      employeeId
      month
      year
      totalHours
      totalCost
      projectAllocations {
        project {
          _id
          name
          code
        }
        hours
        percentage
        cost
      }
    }
  }
`

export const DEVELOPER_KERNEL_QUERY = gql`
  query DeveloperKernel($employeeId: ID!) {
    developerKernel(employeeId: $employeeId) {
      id
      employeeId
      version
      lastUpdated
      overallLevel
      yearsExperience
      currentRole
      seniorityLevel
      targetRole
      targetDate
      targetOverallLevel
      totalProjects
      successfulProjects
      averageProjectRating
      promotionDates
      awards
      certifications
      employee {
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        hourlyRate
        overtimeRate
        resume
        is_active
        is_deleted
        employee_left
        leaving_date
        createdAt
        updatedAt
      }
      languages {
        name
        level
        years
        lastUsed
        projectsCount
        confidence
      }
      databases {
        name
        level
        years
        type
      }
      frameworks {
        name
        level
        years
        projects
      }
      domainExpertise {
        name
        level
        projects
        years
        certifications
        specificKnowledge
      }
      softSkills {
        name
        level
        evidence
        targetLevel
      }
      performanceMetrics {
        velocity
        qualityScore
        bugRate
        reviewTurnaroundHours
        onTimeDelivery
        innovationScore
      }
      gaps {
        id
        category
        skill
        currentLevel
        targetLevel
        gapSize
        priority
        businessImpact
        urgency
        estimatedTimeMonths
      }
      activeInterventions {
        id
        gapId
        type
        name
        startDate
        expectedEndDate
        progress
        status
        timeInvestedHours
      }
      careerMilestones {
        milestone
        targetDate
        status
        progress
        _id
        name
        description
        dueDate
        completedDate
        isCompleted
      }
    }
  }
`

// ============================================
// DEVELOPER KERNEL INTERFACES
// ============================================

export interface PerformanceMetrics {
  velocity?: number | null
  qualityScore?: number | null
  bugRate?: number | null
  reviewTurnaroundHours?: number | null
  onTimeDelivery?: number | null
  innovationScore?: number | null
}

export interface Language {
  name: string
  level: number
  years: number
  lastUsed?: string
  projectsCount?: number
  confidence?: number
}

export interface Database {
  name: string
  level: number
  years: number
  type?: string
}

export interface Framework {
  name: string
  level: number
  years: number
  projects?: number
}

export interface DomainExpertise {
  name: string
  level: number
  projects?: number
  years?: number
  certifications?: string[]
  specificKnowledge?: string[]
}

export interface SoftSkill {
  name: string
  level: number
  evidence?: string[]
  targetLevel?: number
}

export interface Gap {
  id: string
  category: string
  skill: string
  currentLevel: number
  targetLevel: number
  gapSize: number
  priority: string
  businessImpact?: string
  urgency?: string
  estimatedTimeMonths?: number
}

export interface Intervention {
  id: string
  gapId?: string
  type: string
  name?: string
  startDate?: string
  expectedEndDate?: string
  status: string
  progress: number
  timeInvestedHours?: number
}

export interface Milestone {
  milestone: string
  targetDate: string
  status: string
  progress: number
  _id?: string
  name?: string
  description?: string
  dueDate?: string
  completedDate?: string
  isCompleted?: boolean
}

export interface CreateDeveloperKernelInput {
  employeeId?: string | null
  version?: string | null
  overallLevel?: number | null
  yearsExperience?: number | null
  currentRole?: string | null
  seniorityLevel?: number | null
  languages?: Language[] | null
  databases?: Database[] | null
  frameworks?: Framework[] | null
  domainExpertise?: DomainExpertise[] | null
  softSkills?: SoftSkill[] | null
  performanceMetrics?: PerformanceMetrics | null
  targetRole?: string | null
  targetDate?: string | null
  targetOverallLevel?: number | null
  gaps?: Gap[] | null
  activeInterventions?: Intervention[] | null
  careerMilestones?: Milestone[] | null
  totalProjects?: number | null
  successfulProjects?: number | null
  averageProjectRating?: number | null
  promotionDates?: string[] | null
  awards?: string[] | null
  certifications?: string[] | null
}

export interface UpdateDeveloperKernelInput {
  id?: string | null
  version?: string | null
  overallLevel?: number | null
  yearsExperience?: number | null
  currentRole?: string | null
  seniorityLevel?: number | null
  languages?: Language[] | null
  databases?: Database[] | null
  frameworks?: Framework[] | null
  domainExpertise?: DomainExpertise[] | null
  softSkills?: SoftSkill[] | null
  performanceMetrics?: PerformanceMetrics | null
  targetRole?: string | null
  targetDate?: string | null
  targetOverallLevel?: number | null
  gaps?: Gap[] | null
  activeInterventions?: Intervention[] | null
  careerMilestones?: Milestone[] | null
  totalProjects?: number | null
  successfulProjects?: number | null
  averageProjectRating?: number | null
  promotionDates?: string[] | null
  awards?: string[] | null
  certifications?: string[] | null
}

export interface DeveloperKernel {
  id: string
  employeeId: string
  version?: string
  lastUpdated?: string
  overallLevel?: number
  yearsExperience?: number
  currentRole?: string
  seniorityLevel?: number
  targetRole?: string
  targetDate?: string
  targetOverallLevel?: number
  totalProjects?: number
  successfulProjects?: number
  averageProjectRating?: number
  promotionDates?: string[]
  awards?: string[]
  certifications?: string[]
  employee?: Employee
  languages?: Language[]
  databases?: Database[]
  frameworks?: Framework[]
  domainExpertise?: DomainExpertise[]
  softSkills?: SoftSkill[]
  performanceMetrics?: PerformanceMetrics
  gaps?: Gap[]
  activeInterventions?: Intervention[]
  careerMilestones?: Milestone[]
}

// ============================================
// MUTATIONS
// ============================================

export const CREATE_EMPLOYEE_MUTATION = gql`
  mutation CreateEmployee(
    $firstName: String!,
    $lastName: String!,
    $email: String!,
    $countryCode: String,
    $mobile_number: String,
    $joining_date: String,
    $skills: [String],
    $designation: String,
    $department: String,
    $role: ID,
    $shift: Shift,
    $is_active: Boolean,
    $hourlyRate: Float,
    $overtimeRate: Float
  ) {
    createEmployee(
      input: {
        firstName: $firstName
        lastName: $lastName
        email: $email
        countryCode: $countryCode
        mobile_number: $mobile_number
        joining_date: $joining_date
        skills: $skills
        designation: $designation
        department: $department
        role: $role
        shift: $shift
        is_active: $is_active
        hourlyRate: $hourlyRate
        overtimeRate: $overtimeRate
      }
    ) {
      azureId
      azureTenantId
      azureEmail
      azureDisplayName
      ssoEnabled
      lastAzureLogin
      azureGroups
      _id
      firstName
      lastName
      email
      countryCode
      mobile_number
      languages
      joining_date
      skills
      designation
      department
      shift
      hourlyRate
      overtimeRate
      is_active
      is_deleted
      createdAt
      updatedAt
    }
  }
`

export const CREATE_DEVELOPER_KERNEL_MUTATION = gql`
  mutation CreateDeveloperKernel($input: CreateDeveloperKernelInput!) {
    createDeveloperKernel(input: $input) {
      id
      employeeId
      employee {
        _id
        firstName
        lastName
        email
      }
      version
      overallLevel
      yearsExperience
      currentRole
      seniorityLevel
     
      databases {
        name
        level
        years
      }
      frameworks {
        name
        level
        years
        projects
      }
      domainExpertise {
        name
        level
        projects
        specificKnowledge
      }
      softSkills {
        name
        level
      }
      performanceMetrics {
        velocity
        qualityScore
        bugRate
        reviewTurnaroundHours
        onTimeDelivery
        innovationScore
      }
      targetRole
      targetDate
      targetOverallLevel
      gaps {
        id
        category
        skill
        currentLevel
        targetLevel
        gapSize
        priority
      }
      activeInterventions {
        id
        type
        status
        progress
      }
      careerMilestones {
        milestone
        targetDate
        status
        progress
      }
      totalProjects
      successfulProjects
      averageProjectRating
      promotionDates
      awards
      certifications
    }
  }
`

export const UPDATE_EMPLOYEE_MUTATION = gql`
  mutation UpdateEmployee($input: UpdateEmployeeInput!) {
    updateEmployee(input: $input) {
      azureId
      azureTenantId
      azureEmail
      azureDisplayName
      ssoEnabled
      lastAzureLogin
      azureGroups
      _id
      firstName
      lastName
      email
      countryCode
      mobile_number
      languages
      joining_date
      skills
      designation
      department
      shift
      is_active
      is_deleted
      createdAt
      updatedAt
      hourlyRate
      overtimeRate
      role {
        _id
        title
        is_active
      }
    }
  }
`

export const DELETE_EMPLOYEE_MUTATION = gql`
  mutation DeleteEmployee($id: ID!) {
    deleteEmployee(id: $id) {
      azureId
      azureTenantId
      azureEmail
      azureDisplayName
      ssoEnabled
      lastAzureLogin
      azureGroups
      _id
      firstName
      lastName
      email
      countryCode
      mobile_number
      languages
      joining_date
      skills
      designation
      department
      shift
      is_active
      is_deleted
      createdAt
      updatedAt
    }
  }
`

export const TOGGLE_EMPLOYEE_STATUS_MUTATION = gql`
  mutation ToggleEmployeeStatus($id: ID!) {
    toggleEmployeeStatus(id: $id) {
      id
      isActive
    }
  }
`

export const CREATE_EMPLOYEE_FROM_AZURE_MUTATION = gql`
  mutation CreateEmployeeFromAzure($azureData: AzureUserData!) {
    createEmployeeFromAzure(azureData: $azureData) {
      id
      employeeId
      firstName
      lastName
      email
      azureId
      azureGroups
      isActive
      createdAt
    }
  }
`

export const SYNC_EMPLOYEE_AZURE_GROUPS_MUTATION = gql`
  mutation SyncEmployeeAzureGroups($employeeId: ID!, $groups: [String!]!) {
    syncEmployeeAzureGroups(employeeId: $employeeId, groups: $groups) {
      id
      azureGroups
      updatedAt
    }
  }
`

export const UPDATE_EMPLOYEE_AZURE_SETTINGS_MUTATION = gql`
  mutation UpdateEmployeeAzureSettings($employeeId: ID!, $settings: AzureSettingsInput!) {
    updateEmployeeAzureSettings(employeeId: $employeeId, settings: $settings) {
      id
      azureId
      azureGroups
      updatedAt
    }
  }
`

export const PROCESS_AZURE_AUTH_MUTATION = gql`
  mutation ProcessAzureAuth($code: String!, $state: String!) {
    processAzureAuth(code: $code, state: $state) {
      token
      employee {
        id
        employeeId
        firstName
        lastName
        email
        azureId
      }
      isNewEmployee
    }
  }
`

export const CREATE_EMPLOYEE_COST_MUTATION = gql`
  mutation CreateEmployeeCost($input: CreateEmployeeCostInput!) {
    createEmployeeCost(input: $input) {
      _id
      effectiveDate
      endDate
      baseSalary
      currency
      payFrequency
      totalMonthlyCost
      totalAnnualCost
      status
      approvalDate
      notes
      createdAt
      updatedAt
      employee {
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        joining_date
        designation
        role {
          _id
          title
          is_active
        }
        is_active
      }
      costComponents {
        allowances {
          housing
          transport
          meal
          medical
        }
        deductions {
          tax
          socialSecurity
          insurance
        }
        employerContributions {
          socialSecurity
          insurance
          retirement
        }
      }
    }
  }
`

export const UPDATE_EMPLOYEE_COST_MUTATION = gql`
  mutation UpdateEmployeeCost($id: ID!, $input: UpdateEmployeeCostInput!) {
    updateEmployeeCost(id: $id, input: $input) {
      _id
      effectiveDate
      endDate
      baseSalary
      currency
      payFrequency
      totalMonthlyCost
      totalAnnualCost
      status
      approvalDate
      notes
      createdAt
      updatedAt
      employee {
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        is_active
        createdAt
        updatedAt
        role {
          _id
          title
          is_active
        }
      }
      costComponents {
        allowances {
          housing
          transport
          meal
          medical
        }
        deductions {
          tax
          socialSecurity
          insurance
        }
        employerContributions {
          socialSecurity
          insurance
          retirement
        }
      }
    }
  }
`

export const APPROVE_EMPLOYEE_COST_MUTATION = gql`
  mutation ApproveEmployeeCost($id: ID!) {
    approveEmployeeCost(id: $id) {
      _id
      effectiveDate
      endDate
      baseSalary
      currency
      payFrequency
      totalMonthlyCost
      totalAnnualCost
      status
      approvalDate
      notes
      createdAt
      updatedAt
    }
  }
`

export const CALCULATE_EMPLOYEE_HOUR_MUTATION = gql`
  mutation CalculateEmployeeHour($input: CalculateEmployeeHourInput!) {
    calculateEmployeeHour(input: $input) {
      id
      month
      year
      totalRegularHours
      totalOvertimeHours
      totalWorkingDays
      presentDays
      absentDays
      holidays
      weekends
      leaves
      hourlyRate
      overtimeRate
      regularCost
      overtimeCost
      totalCost
      attendanceIds
      status
      approvalStatus
      approvalRemarks
      remarks
      createdAt
      updatedAt
      employee {
        _id
        firstName
        lastName
        email
      }
    }
  }
`

export const EMPLOYEE_HOUR_BY_MONTH_QUERY = gql`
  query EmployeeHourByMonth($employeeId: ID!, $month: Int!, $year: Int!) {
    employeeHourByMonth(employeeId: $employeeId, month: $month, year: $year) {
      id
      month
      year
      totalRegularHours
      totalOvertimeHours
      totalWorkingDays
      presentDays
      absentDays
      holidays
      weekends
      leaves
      hourlyRate
      overtimeRate
      regularCost
      overtimeCost
      totalCost
      attendanceIds
      status
      approvalStatus
      approvalRemarks
      remarks
      createdAt
      updatedAt
      employee {
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        hourlyRate
        overtimeRate
        is_active
        is_deleted
        resume
        createdAt
        updatedAt
      }
    }
  }
`

export const UPDATE_DEVELOPER_KERNEL_MUTATION = gql`
  mutation UpdateDeveloperKernel($input: UpdateDeveloperKernelInput!) {
    updateDeveloperKernel(input: $input) {
        id
        employeeId
        version
        lastUpdated
        overallLevel
        yearsExperience
        currentRole
        seniorityLevel
        targetRole
        targetDate
        targetOverallLevel
        totalProjects
        successfulProjects
        averageProjectRating
        promotionDates
        awards
        certifications
        languages {
            name
            level
            years
            lastUsed
            projectsCount
            confidence
        }
        databases {
            name
            level
            years
            type
        }
        frameworks {
            name
            level
            years
            projects
        }
        domainExpertise {
            name
            level
            projects
            years
            certifications
            specificKnowledge
        }
        softSkills {
            name
            level
            evidence
            targetLevel
        }
        performanceMetrics {
            velocity
            qualityScore
            bugRate
            reviewTurnaroundHours
            onTimeDelivery
            innovationScore
        }
        gaps {
            id
            category
            skill
            currentLevel
            targetLevel
            gapSize
            priority
            businessImpact
            urgency
            estimatedTimeMonths
        }
        careerMilestones {
            milestone
            targetDate
            status
            progress
            _id
            name
            description
            dueDate
            completedDate
            isCompleted
        }
        activeInterventions {
            id
            gapId
            type
            name
            startDate
            expectedEndDate
            progress
            status
            timeInvestedHours
        }
    }
}
`

export const MARK_EMPLOYEE_LEFT_MUTATION = gql`
  mutation MarkEmployeeLeft($input: MarkEmployeeLeftInput!) {
    markEmployeeLeft(input: $input) {
      azureId
      azureTenantId
      azureEmail
      azureDisplayName
      ssoEnabled
      lastAzureLogin
      azureGroups
      _id
      firstName
      lastName
      email
      countryCode
      mobile_number
      languages
      joining_date
      skills
      designation
      department
      shift
      hourlyRate
      overtimeRate
      is_active
      is_deleted
      employee_left
      leaving_date
      createdAt
      updatedAt
    }
  }
`

// Helper types for paginated responses
interface EmployeeEdge {
  azureId?: string
  azureTenantId?: string
  azureEmail?: string
  azureDisplayName?: string
  ssoEnabled?: boolean
  lastAzureLogin?: string
  azureGroups?: string[]
  _id: string
  firstName: string
  lastName: string
  email: string
  countryCode?: string
  mobile_number?: string
  languages?: string[]
  joining_date?: string
  skills?: string[]
  designation?: Designation
  department?: Department
  shift?: string
  is_active: boolean
  is_deleted?: boolean
  createdAt: string
  updatedAt: string
  hourlyRate: string
  overtimeRate: string
  employee_left: boolean
  leaving_date: string
  role?: {
    _id: string
    title: string
    is_active: boolean
  }
  leave_requests?: {
    leveId: string
    status: string
    description: string
  }[]
  documents?: {
    url: string
    type: string
    valid: boolean
  }[]
  resume:string|null
}

interface EmployeesResponse {
  nextCursor?: string
  edges: EmployeeEdge[]
}

interface EmployeesByRoleResponse {
  edges: {
    node: Employee
    cursor: string
  }[]
  pageInfo: {
    hasNextPage: boolean
    endCursor?: string
  }
  totalCount: number
}

interface DeleteResponse {
  success: boolean
  message: string
}

interface AzureSettingsInput {
  azureId?: string
  azureGroups?: string[]
  // Add other Azure settings fields as needed
}

// ============================================
// API SERVICE CLASS
// ============================================

export class EmployeeAPI {
  // Employee Queries
  static async getEmployees(limit: number = 10, department?: string, designation?: string): Promise<EmployeesResponse> {
    const { employees } = await executeGraphQL<{ employees: EmployeesResponse }>(
      EMPLOYEES_QUERY,
      { limit, department, designation }
    )
    return employees
  }

  static async getAllEmployees(): Promise<EmployeesResponse> {
      const { employees } = await executeGraphQL<{ employees: EmployeesResponse }>(
        ALL_EMPLOYEES_QUERY
      )
      return employees
    }

  static async getEmployee(id: string): Promise<EmployeeDetails> {
    const { employee } = await executeGraphQL<{ employee: EmployeeDetails }>(
      EMPLOYEE_QUERY,
      { id }
    )
    return employee
  }

  static async getEmployeesByRole(role: string, first: number = 10, after?: string): Promise<EmployeesByRoleResponse> {
    const { employeesByRole } = await executeGraphQL<{ employeesByRole: EmployeesByRoleResponse }>(
      EMPLOYEES_BY_ROLE_QUERY,
      { role, first, after }
    )
    return employeesByRole
  }

  static async getEmployeeByAzureId(azureId: string): Promise<Employee> {
    const { employeeByAzureId } = await executeGraphQL<{ employeeByAzureId: Employee }>(
      EMPLOYEE_BY_AZURE_ID_QUERY,
      { azureId }
    )
    return employeeByAzureId
  }

  static async checkEmployeeAzureAccess(azureId: string): Promise<{
    hasAccess: boolean
    employee: Employee
    requiredGroups: string[]
    userGroups: string[]
  }> {
    const { checkEmployeeAzureAccess } = await executeGraphQL<{
      checkEmployeeAzureAccess: {
        hasAccess: boolean
        employee: Employee
        requiredGroups: string[]
        userGroups: string[]
      }
    }>(
      CHECK_EMPLOYEE_AZURE_ACCESS_QUERY,
      { azureId }
    )
    return checkEmployeeAzureAccess
  }

  static async getAzureAuthConfig(): Promise<AzureAuthConfig> {
    const { azureAuthConfig } = await executeGraphQL<{ azureAuthConfig: AzureAuthConfig }>(
      AZURE_AUTH_CONFIG_QUERY
    )
    return azureAuthConfig
  }

  // Get current user's profile
  static async getProfile(): Promise<EmployeeDetails | any> {
    // Mock profile data for development - in production this would call API
    return {
      id: '1',
      employeeId: 'EMP001',
      firstName: 'Milan',
      lastName: 'Developer',
      email: 'milan@atcuality.com',
      phone: '+1 234-567-8900',
      designation: {
        id: '1',
        name: 'Senior Developer',
        code: 'SR_DEV',
      },
      department: {
        id: '1',
        name: 'Engineering',
        code: 'ENG',
      },
      organization: {
        id: '1',
        name: 'InstaDev',
        code: 'INSTA',
      },
      skills: ['React', 'TypeScript', 'GraphQL', 'Node.js'],
      certifications: ['AWS Certified Developer', 'Microsoft Azure Fundamentals'],
      azureId: 'milan@atcuality.com',
      isActive: true,
      createdAt: '2024-01-01',
      updatedAt: '2024-01-15'
    }
  }

  // Employee Mutations
  static async createEmployee(employeeData: {
    firstName?: string;
    lastName?: string;
    email?: string;
    countryCode?: string;
    mobile_number?: string;
    joining_date?: string;
    skills?: string[];
    designation?: string;
    department?: string;
    role?: string;
    shift?: string;
    is_active?: boolean;
    hourlyRate?: number;
    overtimeRate?: number;
  }): Promise<Employee> {
    const { createEmployee } = await executeGraphQL<{ createEmployee: Employee }>(
      CREATE_EMPLOYEE_MUTATION,
      {
        firstName: employeeData.firstName,
        lastName: employeeData.lastName,
        email: employeeData.email,
        countryCode: employeeData.countryCode,
        mobile_number: employeeData.mobile_number,
        joining_date: employeeData.joining_date,
        skills: employeeData.skills,
        designation: employeeData.designation,
        department: employeeData.department,
        role: employeeData.role ? employeeData.role: undefined,
        hourlyRate: employeeData.hourlyRate,
        overtimeRate: employeeData.overtimeRate,  
        // shift: employeeData.shift || 'DAY', // Default to DAY shift
        is_active: employeeData.is_active !== undefined ? employeeData.is_active : true
      }
    )
    return createEmployee
  }

  static async updateEmployee(input: UpdateEmployeeInput): Promise<Employee> {
    const { updateEmployee } = await executeGraphQL<{ updateEmployee: Employee }>(
      UPDATE_EMPLOYEE_MUTATION,
      { input }
    )
    return updateEmployee
  }

  static async deleteEmployee(id: string): Promise<Employee> {
    const { deleteEmployee } = await executeGraphQL<{ deleteEmployee: Employee }>(
      DELETE_EMPLOYEE_MUTATION,
      { id }
    )
    return deleteEmployee
  }

  static async toggleEmployeeStatus(id: string): Promise<Employee> {
    const { toggleEmployeeStatus } = await executeGraphQL<{ toggleEmployeeStatus: Employee }>(
      TOGGLE_EMPLOYEE_STATUS_MUTATION,
      { id }
    )
    return toggleEmployeeStatus
  }

  static async markEmployeeLeft(input: MarkEmployeeLeftInput): Promise<Employee> {
    const { markEmployeeLeft } = await executeGraphQL<{ markEmployeeLeft: Employee }>(
      MARK_EMPLOYEE_LEFT_MUTATION,
      { input }
    )
    return markEmployeeLeft
  }

  // Developer Kernel Methods
  static async createDeveloperKernel(input: CreateDeveloperKernelInput): Promise<DeveloperKernel> {
    const { createDeveloperKernel } = await executeGraphQL<{ createDeveloperKernel: DeveloperKernel }>(
      CREATE_DEVELOPER_KERNEL_MUTATION,
      { input }
    )
    return createDeveloperKernel
  }

  static async getDeveloperKernel(employeeId: string): Promise<DeveloperKernel | null> {
    try {
      const { developerKernel } = await executeGraphQL<{ developerKernel: DeveloperKernel }>(
        DEVELOPER_KERNEL_QUERY,
        { employeeId }
      )
      return developerKernel
    } catch (error) {
      console.warn('Developer kernel not found for employee:', employeeId, error)
      return null
    }
  }

  static async updateDeveloperKernel(input: UpdateDeveloperKernelInput): Promise<DeveloperKernel> {
    const { updateDeveloperKernel } = await executeGraphQL<{ updateDeveloperKernel: DeveloperKernel }>(
      UPDATE_DEVELOPER_KERNEL_MUTATION,
      { input }
    )
    return updateDeveloperKernel
  }

  // Azure Integration
  static async createEmployeeFromAzure(azureData: Partial<Employee>): Promise<Employee> {
    const { createEmployeeFromAzure } = await executeGraphQL<{ createEmployeeFromAzure: Employee }>(
      CREATE_EMPLOYEE_FROM_AZURE_MUTATION,
      { azureData }
    )
    return createEmployeeFromAzure
  }

  static async syncEmployeeAzureGroups(employeeId: string, groups: string[]): Promise<Employee> {
    const { syncEmployeeAzureGroups } = await executeGraphQL<{ syncEmployeeAzureGroups: Employee }>(
      SYNC_EMPLOYEE_AZURE_GROUPS_MUTATION,
      { employeeId, groups }
    )
    return syncEmployeeAzureGroups
  }

  static async updateEmployeeAzureSettings(employeeId: string, settings: AzureSettingsInput): Promise<Employee> {
    const { updateEmployeeAzureSettings } = await executeGraphQL<{ updateEmployeeAzureSettings: Employee }>(
      UPDATE_EMPLOYEE_AZURE_SETTINGS_MUTATION,
      { employeeId, settings }
    )
    return updateEmployeeAzureSettings
  }

  static async processAzureAuth(code: string, state: string): Promise<{
    token: string
    employee: Employee
    isNewEmployee: boolean
  }> {
    const { processAzureAuth } = await executeGraphQL<{
      processAzureAuth: {
        token: string
        employee: Employee
        isNewEmployee: boolean
      }
    }>(
      PROCESS_AZURE_AUTH_MUTATION,
      { code, state }
    )
    if (processAzureAuth.token) {
      localStorage.setItem('authToken', processAzureAuth.token)
    }
    return processAzureAuth
  }

  // Employee Cost Management
  static async getEmployeeCosts(limit: number = 100, cursor?: string, filter?: EmployeeCostFilter): Promise<EmployeeCostsResponse> {
    const { employeeCosts } = await executeGraphQL<{ employeeCosts: EmployeeCostsResponse }>(
      EMPLOYEE_COSTS_QUERY,
      { limit, cursor, filter }
    )
    return employeeCosts
  }

  static async createEmployeeCost(input: CreateEmployeeCostInput): Promise<EmployeeCost> {
    const { createEmployeeCost } = await executeGraphQL<{ createEmployeeCost: EmployeeCost }>(
      CREATE_EMPLOYEE_COST_MUTATION,
      { input }
    )
    return createEmployeeCost
  }

  static async updateEmployeeCost(id: string, input: UpdateEmployeeCostInput): Promise<EmployeeCost> {
    const { updateEmployeeCost } = await executeGraphQL<{ updateEmployeeCost: EmployeeCost }>(
      UPDATE_EMPLOYEE_COST_MUTATION,
      { id, input }
    )
    return updateEmployeeCost
  }

  static async approveEmployeeCost(id: string): Promise<EmployeeCost> {
    const { approveEmployeeCost } = await executeGraphQL<{ approveEmployeeCost: EmployeeCost }>(
      APPROVE_EMPLOYEE_COST_MUTATION,
      { id }
    )
    return approveEmployeeCost
  }

  // Project-based Cost Tracking
  static async getProjectEmployeeCost(
    employeeId: string,
    projectId: string,
    startDate: string,
    endDate: string
  ): Promise<ProjectEmployeeCost> {
    const { projectEmployeeCost } = await executeGraphQL<{ projectEmployeeCost: ProjectEmployeeCost }>(
      PROJECT_EMPLOYEE_COST_QUERY,
      { employeeId, projectId, startDate, endDate }
    )
    return projectEmployeeCost
  }

  static async getProjectCostSummary(
    projectId: string,
    startDate: string,
    endDate: string
  ): Promise<ProjectCostSummary> {
    const { projectCostSummary } = await executeGraphQL<{ projectCostSummary: ProjectCostSummary }>(
      PROJECT_COST_SUMMARY_QUERY,
      { projectId, startDate, endDate }
    )
    return projectCostSummary
  }

  static async getEmployeeProjectAllocation(
    employeeId: string,
    month: number,
    year: number
  ): Promise<EmployeeProjectAllocation> {
    const { employeeProjectAllocation } = await executeGraphQL<{ employeeProjectAllocation: EmployeeProjectAllocation }>(
      EMPLOYEE_PROJECT_ALLOCATION_QUERY,
      { employeeId, month, year }
    )
    return employeeProjectAllocation
  }

  // Employee Hour Calculation
  static async calculateEmployeeHour(input: CalculateEmployeeHourInput): Promise<EmployeeHourCalculation> {
    const { calculateEmployeeHour } = await executeGraphQL<{ calculateEmployeeHour: EmployeeHourCalculation }>(
      CALCULATE_EMPLOYEE_HOUR_MUTATION,
      { input }
    )
    return calculateEmployeeHour
  }

  // Get Employee Hour By Month
  static async getEmployeeHourByMonth(employeeId: string, month: number, year: number): Promise<EmployeeHourByMonth | null> {
    try {
      const { employeeHourByMonth } = await executeGraphQL<{ employeeHourByMonth: EmployeeHourByMonth }>(
        EMPLOYEE_HOUR_BY_MONTH_QUERY,
        { employeeId, month, year }
      )
      return employeeHourByMonth
    } catch (error) {
      console.warn('Employee hour data not found for this month:', error)
      return null
    }
  }

  // Project Cost Tracking Methods
  static async getAllProjectsCostSummary(startDate: string, endDate: string): Promise<ProjectCostSummary[]> {
    try {
      // Import ProjectAPI dynamically to avoid circular dependencies
      const { ProjectAPI } = await import('./project')

      // Get all active projects from the real ProjectAPI
      const projectsResponse = await ProjectAPI.getProjects({
        limit: 50,
        status: 'IN_PROGRESS'
      })
      const projects = projectsResponse.edges || []
      const projectCostSummaries: ProjectCostSummary[] = []

      // Fetch cost summary for each project
      for (const project of projects) {
        try {
          const costSummary = await this.getProjectCostSummary(project._id, startDate, endDate)
          projectCostSummaries.push(costSummary)
        } catch (error) {
          console.warn(`Failed to get cost summary for project ${project._id}:`, error)
          // Continue with other projects
        }
      }

      return projectCostSummaries
    } catch (error) {
      console.error('Error fetching all projects cost summary:', error)
      return []
    }
  }

  static async getProjectSpendingAnalytics(startDate: string, endDate: string): Promise<{
    totalBudget: number
    totalSpent: number
    projectsOnBudget: number
    projectsOverBudget: number
    projectsUnderBudget: number
    projectsAtRisk: number
    costSavings: number
    projectedOverrun: number
  }> {
    try {
      const projectSummaries = await this.getAllProjectsCostSummary(startDate, endDate)

      let totalBudget = 0
      let totalSpent = 0
      let projectsOnBudget = 0
      let projectsOverBudget = 0
      let projectsUnderBudget = 0
      let projectsAtRisk = 0
      let costSavings = 0
      let projectedOverrun = 0

      // Import ProjectAPI dynamically to avoid circular dependencies
      const { ProjectAPI } = await import('./project')

      // Get all active projects from the real ProjectAPI
      const projectsResponse = await ProjectAPI.getProjects({
        limit: 50,
        status: 'IN_PROGRESS'
      })
      const projects = projectsResponse.edges || []

      projectSummaries.forEach((summary, index) => {
        const project = projects[index]
        if (!project) return

        const budget = project.budget?.estimated || 0
        const spent = summary.totalCost || 0
        const utilization = budget > 0 ? (spent / budget) * 100 : 0

        totalBudget += budget
        totalSpent += spent

        // Determine project status
        if (utilization <= 80) {
          projectsUnderBudget++
          costSavings += (budget - spent)
        } else if (utilization <= 100) {
          projectsOnBudget++
        } else if (utilization <= 110) {
          projectsAtRisk++
        } else {
          projectsOverBudget++
          projectedOverrun += (spent - budget)
        }
      })

      return {
        totalBudget,
        totalSpent,
        projectsOnBudget,
        projectsOverBudget,
        projectsUnderBudget,
        projectsAtRisk,
        costSavings,
        projectedOverrun
      }
    } catch (error) {
      console.error('Error calculating project spending analytics:', error)
      return {
        totalBudget: 0,
        totalSpent: 0,
        projectsOnBudget: 0,
        projectsOverBudget: 0,
        projectsUnderBudget: 0,
        projectsAtRisk: 0,
        costSavings: 0,
        projectedOverrun: 0
      }
    }
  }

  static async calculateProjectProfitability(
    projectId: string,
    startDate: string,
    endDate: string,
    contractValue?: number
  ): Promise<{
    projectId: string
    revenue: number
    totalCost: number
    profit: number
    profitMargin: number
    hoursSpent: number
    costPerHour: number
  }> {
    try {
      const costSummary = await this.getProjectCostSummary(projectId, startDate, endDate)
      const revenue = contractValue || 0
      const totalCost = costSummary.totalCost
      const profit = revenue - totalCost
      const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0
      const hoursSpent = costSummary.totalHours
      const costPerHour = hoursSpent > 0 ? totalCost / hoursSpent : 0

      return {
        projectId,
        revenue,
        totalCost,
        profit,
        profitMargin,
        hoursSpent,
        costPerHour
      }
    } catch (error) {
      console.error('Error calculating project profitability:', error)
      return {
        projectId,
        revenue: 0,
        totalCost: 0,
        profit: 0,
        profitMargin: 0,
        hoursSpent: 0,
        costPerHour: 0
      }
    }
  }

  static async getMonthlyProjectCosts(
    projectId: string,
    year: number
  ): Promise<{
    month: string
    planned: number
    actual: number
    variance: number
  }[]> {
    try {
      const monthlyData = []
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

      for (let month = 1; month <= 12; month++) {
        const startDate = new Date(year, month - 1, 1).toISOString().split('T')[0]
        const endDate = new Date(year, month, 0).toISOString().split('T')[0]

        try {
          const costSummary = await this.getProjectCostSummary(projectId, startDate, endDate)
          const actual = costSummary.totalCost || 0

          // For planned, we'd typically get this from project planning data
          // For now, we'll estimate based on budget allocation
          const { ProjectAPI } = await import('./project')
          const project = await ProjectAPI.getProject(projectId)
          const planned = project?.budget?.estimated ? project.budget.estimated / 12 : 0

          monthlyData.push({
            month: monthNames[month - 1],
            planned,
            actual,
            variance: actual - planned
          })
        } catch (error) {
          console.warn(`Failed to get costs for ${monthNames[month - 1]} ${year}:`, error)
          monthlyData.push({
            month: monthNames[month - 1],
            planned: 0,
            actual: 0,
            variance: 0
          })
        }
      }

      return monthlyData
    } catch (error) {
      console.error('Error fetching monthly project costs:', error)
      return []
    }
  }

  // Optimized method to get all project spending data in one batch call
  static async getOptimizedProjectSpendingData(startDate: string, endDate: string): Promise<{
    projects: Array<{
      project: any
      costSummary: ProjectCostSummary
      monthlySpending: {
        month: string
        planned: number
        actual: number
        variance: number
      }[]
      profitability: {
        projectId: string
        revenue: number
        totalCost: number
        profit: number
        profitMargin: number
        hoursSpent: number
        costPerHour: number
      }
    }>
    analytics: {
      totalBudget: number
      totalSpent: number
      projectsOnBudget: number
      projectsOverBudget: number
      projectsUnderBudget: number
      projectsAtRisk: number
      costSavings: number
      projectedOverrun: number
    }
  }> {
    try {
      // Import ProjectAPI dynamically to avoid circular dependencies
      const { ProjectAPI } = await import('./project')

      // Single call to get all active projects
      const projectsResponse = await ProjectAPI.getProjects({
        limit: 100, // Increased limit to get more projects
        status: 'IN_PROGRESS'
      })
      const projects = projectsResponse.edges || []

      // Batch process all project data with fewer API calls
      const projectDataPromises = projects.map(async (project) => {
        try {
          // Get cost summary for this project
          const costSummary = await this.getProjectCostSummary(project._id, startDate, endDate)

          // Calculate monthly spending using frontend calculations instead of API calls
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          const estimatedMonthlyBudget = project.budget?.estimated ? project.budget.estimated / 12 : 0

          // Generate monthly spending based on period breakdown if available
          const monthlySpending = monthNames.map((month) => {
            const monthData = costSummary.periodBreakdown?.find(p => p.month === month) || { month, hours: 0, cost: 0 }
            return {
              month,
              planned: estimatedMonthlyBudget,
              actual: monthData.cost || 0,
              variance: (monthData.cost || 0) - estimatedMonthlyBudget
            }
          })

          // Calculate profitability using frontend calculations
          const revenue = project.budget?.estimated || 0
          const totalCost = costSummary.totalCost
          const profit = revenue - totalCost
          const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0
          const hoursSpent = costSummary.totalHours
          const costPerHour = hoursSpent > 0 ? totalCost / hoursSpent : 0

          const profitability = {
            projectId: project._id,
            revenue,
            totalCost,
            profit,
            profitMargin,
            hoursSpent,
            costPerHour
          }

          return {
            project,
            costSummary,
            monthlySpending,
            profitability
          }
        } catch (error) {
          console.warn(`Failed to get data for project ${project._id}:`, error)
          // Return default data for failed projects
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          return {
            project,
            costSummary: {
              projectId: project._id,
              totalCost: 0,
              totalHours: 0,
              employeeCosts: [],
              periodBreakdown: []
            },
            monthlySpending: monthNames.map(month => ({
              month,
              planned: 0,
              actual: 0,
              variance: 0
            })),
            profitability: {
              projectId: project._id,
              revenue: 0,
              totalCost: 0,
              profit: 0,
              profitMargin: 0,
              hoursSpent: 0,
              costPerHour: 0
            }
          }
        }
      })

      // Wait for all project data to be processed
      const projectsData = await Promise.all(projectDataPromises)

      // Calculate analytics on the frontend using the fetched data
      let totalBudget = 0
      let totalSpent = 0
      let projectsOnBudget = 0
      let projectsOverBudget = 0
      let projectsUnderBudget = 0
      let projectsAtRisk = 0
      let costSavings = 0
      let projectedOverrun = 0

      projectsData.forEach(({ project, costSummary }) => {
        const budget = project.budget?.estimated || 0
        const spent = costSummary.totalCost || 0
        const utilization = budget > 0 ? (spent / budget) * 100 : 0

        totalBudget += budget
        totalSpent += spent

        // Determine project status using the same logic as the page component
        if (utilization <= 80) {
          projectsUnderBudget++
          costSavings += (budget - spent)
        } else if (utilization <= 100) {
          projectsOnBudget++
        } else if (utilization <= 110) {
          projectsAtRisk++
        } else {
          projectsOverBudget++
          projectedOverrun += (spent - budget)
        }
      })

      const analytics = {
        totalBudget,
        totalSpent,
        projectsOnBudget,
        projectsOverBudget,
        projectsUnderBudget,
        projectsAtRisk,
        costSavings,
        projectedOverrun
      }

      return {
        projects: projectsData,
        analytics
      }
    } catch (error) {
      console.error('Error fetching optimized project spending data:', error)
      return {
        projects: [],
        analytics: {
          totalBudget: 0,
          totalSpent: 0,
          projectsOnBudget: 0,
          projectsOverBudget: 0,
          projectsUnderBudget: 0,
          projectsAtRisk: 0,
          costSavings: 0,
          projectedOverrun: 0
        }
      }
    }
  }
}